import { useBrowserLocation } from "@folio-2026/browser-location"
import Experience from "@folio-2026/experience"
import { getGLTFResources } from "@folio-2026/resources"
import Section from "@folio-2026/section"
import { EVENTS, lerp, Sky3 } from "@folio-2026/shared"
import Smoke from "@folio-2026/shared/smoke"
import TextFollowingMouse, {
  type MSDFFont,
} from "@folio-2026/shared/textfollowingmouse"
import gsap from "gsap"
import { InteractionManager } from "three.interactive"
import { dof } from "three/examples/jsm/tsl/display/DepthOfFieldNode.js"
import { pass, uniform } from "three/tsl"
import * as THREE from "three/webgpu"

import AboutCTA from "./AboutCTA"
import AboutLeaf from "./AboutLeaf"
import BackCTA from "./BackCTA"
import Buildings from "./Buildings"
import LoadingSectionCamera from "./Camera"
import Curtain from "./Curtain"
import EnterCTA from "./EnterCTA"
import Fog from "./Fog"
import Highway from "./Highway"
import Light from "./Light"
import Man from "./Man"
import Neon from "./Neon"
import PoliceCar from "./PoliceCar"
import RainWithPuddle from "./RainWithPuddle"
import Roof from "./Roof"
import { cameraMovements, RENDER_ORDER } from "./const"
import sources from "./sources"

export default class LoaderSection extends Section {
  name = "loader"

  postProcessing!: THREE.PostProcessing

  settings = {
    dofFocusDistance: uniform(2.83),
    dofFocalLength: uniform(1.52),
    dofBokehScale: uniform(3.7),

    emissiveIntensity: uniform(0.2),
  }

  fog!: Fog
  light!: Light
  interactionManager!: InteractionManager

  curtain!: Curtain
  sky!: Sky3
  roof!: Roof
  highway!: Highway
  policeCar!: PoliceCar
  buildings!: Buildings
  man!: Man
  rainWithPuddle!: RainWithPuddle
  neon!: Neon

  enterCTA!: EnterCTA
  aboutCTA!: AboutCTA
  backCTA!: BackCTA
  smoke!: Smoke
  aboutLeaf!: AboutLeaf
  closeText!: TextFollowingMouse
  private hasSetCloseTextPosition = false
  private loaderTextsLeft = false

  /** Tracks loader text+CTA state to avoid redundant reveal/leave (same pattern as Works). */
  private loaderTextState: "revealed" | "revealing" | "leaving" | "left" =
    "left"
  private loaderTextRevealedDelay?: ReturnType<typeof gsap.delayedCall>
  private loaderTextLeftDelay?: ReturnType<typeof gsap.delayedCall>

  declare camera: LoadingSectionCamera

  constructor() {
    super({
      name: "loader",
      sources: sources,
      folderName: "⛳ Loader scene",
      expanded: false,
    })

    this.experience = Experience.getInstance()
    this.sizes = this.experience.sizes

    this.setDebug()
    this.setTextSceneDebug()

    this.aboutLeaf = new AboutLeaf({
      textScene: this.textScene,
      debugFolder: this.debugFolder,
    })

    this.closeText = new TextFollowingMouse({
      scene: this.textScene.scene,
      atlas: this.experience.globalResources
        .atlasUbuntuSansMonoRegular as THREE.Texture,
      font: this.experience.globalResources.bmfontUbuntuMonoRegular as MSDFFont,
      text: "[ close ]",
      startHidden: true,
      onClick: () => {
        this.experience.navigate("/")
      },
      noiseTexture: this.experience.globalResources.perlin as THREE.Texture,
    })

    /**
     * Enter CTA
     */
    this.enterCTA = new EnterCTA({
      textScene: this.textScene,
    })

    /**
     * About CTA
     */
    this.aboutCTA = new AboutCTA({
      textScene: this.textScene,
    })

    /**
     * Back CTA
     */
    this.backCTA = new BackCTA({
      textScene: this.textScene,
    })

    this.experience.on(EVENTS.Loader.CurtainRemoved, this.onEntered.bind(this))

    this.experience.on(EVENTS.About.Exiting, this.onEntered.bind(this))

    this.experience.on(EVENTS.Loader.CurtainRemoved, () => {
      this.toggleTexts("-1")
    })

    this.experience.on(EVENTS.About.Entered, () => {
      if (this.experience.pointer.primaryInput !== "mouse") {
        this.closeText.leave()
        this.hasSetCloseTextPosition = false
        return
      }

      this.hasSetCloseTextPosition = false
      this.closeText.reveal()
    })

    this.experience.on(EVENTS.About.Exiting, () => {
      this.closeText.leave()
      this.hasSetCloseTextPosition = false
    })

    this.experience.on(EVENTS.Loader.CurtainRemoved, () => {
      this.createAudio("intro-soundtrack")
      this.playAudio()
    })

    this.experience.on(EVENTS.Loader.Exiting, (e) => {
      const { progress, direction } = e as {
        progress: number
        direction: string
      }

      this.toggleTexts(direction)

      const volumeLevel = (1 - progress) * this.experience.volume

      this.setVolume(volumeLevel)
    })

    this.experience.on(EVENTS.Audio.SetVolume, (e: unknown) => {
      const { volume } = e as { volume: number }

      if (this.experience.scrollInfo.currentSection?.name === this.name) {
        this.setVolume(volume)
      }
    })
  }

  init() {
    super.init()

    this.camera = new LoadingSectionCamera({ scene: this.scene })
    this.setupCamera()

    this.interactionManager = new InteractionManager(
      this.renderer.instance,
      this.camera.camera,
      this.renderer.instance.domElement,
    )

    //
    // Curtain
    this.curtain = new Curtain({
      scene: this.scene,
      resources: this.resources,
    })

    //
    // Sky, Rain, Buildings, Man ....
    this.setupScene()

    //
    // Events listeners
    if (this.man) {
      this.man.listenEvents()
    }

    this.setPostProcessing()
  }

  eventListeners() {
    super.eventListeners()

    this.experience.on(EVENTS.Scroll.Section.StartUpdate, (e: unknown) => {
      const { name } = e as { name: string }
      if (name !== this.name) {
        return
      }
      this.resetTexts()
      // Do not reset about CTA here: we only get StartUpdate when entering the
      // loader (scroll up). Exiting(-1) may have already started the reveal
      // tween; resetting would kill it. About CTA is reset on Loader.Exited.
    })

    this.experience.on(EVENTS.About.Entered, () => {
      this.loaderTextRevealedDelay?.kill()
      this.loaderTextRevealedDelay = undefined
      this.loaderTextLeftDelay?.kill()
      this.loaderTextLeftDelay = undefined
      this.loaderTextState = "left"
      this.leaveTextsAndCTA()
    })

    this.experience.on(EVENTS.About.Exiting, () => {
      this.toggleTexts("-1")
    })

    this.experience.on(EVENTS.Loader.Exited, () => {
      this.loaderTextRevealedDelay?.kill()
      this.loaderTextRevealedDelay = undefined
      this.loaderTextLeftDelay?.kill()
      this.loaderTextLeftDelay = undefined
      this.loaderTextState = "left"
      this.resetTexts()
      this.aboutCTA.resetAboutCTA()
      this.loaderTextsLeft = true
    })

    useBrowserLocation.subscribe((state, prevState) => {
      const pathname = state.pathname
      const prevPathname = prevState?.pathname
      //
      // CLICK
      if (pathname === "/about" && prevPathname === "/") {
        gsap.to(this.settings.dofFocusDistance, {
          value: 0.87,
          delay: 1,
          duration: 1,
          onStart: () => {
            this.man.plusPatternEnabled.value = 1
          },
        })
        gsap.to(this.settings.dofFocalLength, {
          value: 0.22,
          delay: 1,
          duration: 1,
        })
        gsap.to(this.settings.dofBokehScale, {
          value: 5,
          delay: 1,
          duration: 1,
        })
      }

      if (pathname === "/" && prevPathname === "/about") {
        gsap.to(this.settings.dofFocusDistance, {
          value: 2.83,
          delay: 1,
          duration: 1,
          onStart: () => {
            this.man.plusPatternEnabled.value = 0
          },
        })
        gsap.to(this.settings.dofFocalLength, {
          value: 1.52,
          delay: 1,
          duration: 1,
        })
        gsap.to(this.settings.dofBokehScale, {
          value: 3.7,
          delay: 1,
          duration: 1,
        })
      }
    })
  }

  toggleTexts(direction: string) {
    const directionNum = Number(direction)
    // Exiting loader: scroll down = direction 1 = leave; scroll up = direction -1 = reveal
    const shouldReveal = directionNum <= 0

    if (shouldReveal) {
      if (
        this.loaderTextState === "revealed" ||
        this.loaderTextState === "revealing"
      ) {
        return
      }
      this.loaderTextLeftDelay?.kill()
      this.loaderTextLeftDelay = undefined
      this.loaderTextState = "revealing"
      this.revealTextsAndCTA()
      const count = this.textScene.texts.filter(
        (t) => t.name === "loader",
      ).length
      const totalRevealTime =
        (count > 0 ? (count - 1) * LoaderSection.REVEAL_STAGGER_DURATION : 0) +
        0.6 +
        1 +
        1.5 +
        0.2
      this.loaderTextRevealedDelay?.kill()
      this.loaderTextRevealedDelay = gsap.delayedCall(totalRevealTime, () => {
        this.loaderTextState = "revealed"
        this.loaderTextRevealedDelay = undefined
      })
    } else {
      if (
        this.loaderTextState === "leaving" ||
        this.loaderTextState === "left"
      ) {
        return
      }
      this.loaderTextRevealedDelay?.kill()
      this.loaderTextRevealedDelay = undefined
      this.loaderTextState = "leaving"
      this.leaveTextsAndCTA()
      this.loaderTextLeftDelay?.kill()
      this.loaderTextLeftDelay = gsap.delayedCall(1.5, () => {
        this.loaderTextState = "left"
        this.loaderTextLeftDelay = undefined
      })
    }
  }

  setupCamera() {
    this.camera.camera.name = "loader-camera"

    // Check if we are at / or /about
    const pathname = useBrowserLocation.getState().pathname

    const camMov = cameraMovements.get(pathname === "/" ? "loading" : "about")
    this.camera.camera.position.set(
      camMov.positionX,
      camMov.positionY,
      camMov.positionZ,
    )
    this.camera.camera.lookAt(camMov.lookAtX, camMov.lookAtY, camMov.lookAtZ)
  }

  setupScene() {
    const resources = this.resources

    const gltfResources = getGLTFResources(this.experience.globalResources)

    // if (this.debug.active) {
    // const axesHelper = new THREE.AxesHelper(5)
    // this.scene.add(axesHelper)
    // }

    /**
     * Fog
    this.fog = new Fog({
      scene: this.scene,
      debugFolder: this.debugFolder,
    })
    */

    /**
     * Sky (SkyMesh / Preetham)
     */
    this.sky = new Sky3({
      scene: this.scene,
      debugFolder: this.debugFolder,
      onExposureChange: (value) => {
        this.renderer.instance.toneMappingExposure = value
      },
    })

    /**
     * Rain with Puddle
     */
    this.rainWithPuddle = new RainWithPuddle({
      scene: this.scene,
      camera: this.camera.camera,
      renderer: this.renderer.instance,
      map: this.resources.items["balcony-floor-map"] as THREE.Texture,
      normal: this.resources.items["balcony-floor-normal"] as THREE.Texture,
      maxParticleCount: 2000,
      renderOrder: RENDER_ORDER.RAIN,
      debugFolder: this.debugFolder,
    })

    /**
     * Smoke
     */
    this.smoke = new Smoke({
      smokeTexture: resources.items["smoke"] as THREE.Texture,
      scene: this.scene,
      debugFolder: this.debugFolder,
    })
    this.smoke = new Smoke({
      smokeTexture: resources.items["smoke"] as THREE.Texture,
      scene: this.scene,
      debugFolder: this.debugFolder,
      axisAlignment: "y",
    })

    /**
     * Man
     */
    this.man = new Man({
      scene: this.scene,
      debugFolder: this.debugFolder,
      resources: {
        ...this.experience.globalResources,
        ...this.resources.items,
      },
    })

    // const manModel = this.experience.globalResources.man.scene as THREE.Group
    // manModel.position.set(0, 0, 0)
    // this.scene.add(manModel)

    /**
     * Roof
     */
    this.roof = new Roof({
      scene: this.scene,
      resources: gltfResources,
      debugFolder: this.debugFolder,
      camera: this.camera.camera,
    })

    /**
     * Buildings
     */
    this.buildings = new Buildings({
      scene: this.scene,
      resources: gltfResources,
      debugFolder: this.debugFolder,
    })

    this.neon = new Neon({
      scene: this.scene,
      resources: gltfResources,
      textureResources: this.resources.items as {
        [key: string]: THREE.Texture
      },
      debugFolder: this.debugFolder,
    })

    /**
     * Highway
     */
    this.highway = new Highway({
      scene: this.scene,
      resources: gltfResources,
      debugFolder: this.debugFolder,
    })

    /**
     * Police Car
     */
    this.policeCar = new PoliceCar({
      scene: this.scene,
      resources: gltfResources,
      debugFolder: this.debugFolder,
    })

    /**
     * Lights
     */
    this.light = new Light({
      scene: this.scene,
      debugFolder: this.debugFolder,
    })
  }

  setPostProcessing(): void {
    super.setPostProcessing()

    this.postProcessing = new THREE.PostProcessing(this.renderer.instance)
    /**
     * Whether the default output tone mapping and color
     * space transformation should be enabled or not.
     *
     * It is enabled by default by it must be disabled when
     * effects must be executed after tone mapping and color
     * space conversion. A typical example is FXAA which
     * requires sRGB input.
     *
     * When set to `false`, the app must control the output
     * transformation with `RenderOutputNode`.
     * */
    // this.postProcessing.outputColorTransform = false

    const scenePass = pass(this.scene, this.camera.camera)

    const outputPass = scenePass.getTextureNode()
    const scenePassViewZ = scenePass.getViewZNode()

    const dofPass = dof(
      outputPass,
      scenePassViewZ,
      this.settings.dofFocusDistance, // focus distance
      this.settings.dofFocalLength, // focal length
      this.settings.dofBokehScale, // bokeh scale
    )

    const color = dofPass.add(this.textScene.postProcessing.outputNode)

    // const finalColor = blendColor(
    //   this.textScene.postProcessing.outputNode,
    //   dofPass.add(bloomPass),
    // )

    this.postProcessing.outputNode = color
    // this.postProcessing.outputNode = scenePass

    this.setPPDebug()

    // this.postProcessing.outputNode = dofPass

    // this.postProcessing.outputNode = outputPass.add( bloomPass );
  }

  setPPDebug() {
    if (!this.debugFolder) {
      return
    }

    const pPFolder = this.debugFolder!.addFolder({
      title: "💅 Post Processing",
      expanded: false,
    })

    const dof = {
      distance: this.settings.dofFocusDistance.value,
      length: this.settings.dofFocalLength.value,
      scale: this.settings.dofBokehScale.value,
    }

    pPFolder
      .addBinding(dof, "distance", {
        label: "DOF distance",
        min: 0,
        max: 5,
        step: 0.01,
      })
      .on("change", (ev) => {
        this.settings.dofFocusDistance.value = ev.value
      })

    pPFolder
      .addBinding(dof, "length", {
        label: "DOF length",
        min: 0,
        max: 5,
        step: 0.01,
      })
      .on("change", (ev) => {
        this.settings.dofFocalLength.value = ev.value
      })

    pPFolder
      .addBinding(dof, "scale", {
        label: "DOF bokeh scale",
        min: 0,
        max: 10,
        step: 0.01,
      })
      .on("change", (ev) => {
        this.settings.dofBokehScale.value = ev.value
      })
  }

  onEntered() {
    const pathname = window.location.pathname
    if (pathname !== "/") {
      return
    }

    this.experience.lenis?.start()
  }

  private static readonly REVEAL_STAGGER_DURATION = 0.12

  /**
   * Refresh bounds from DOM and position loader text meshes.
   * Loader content is fixed in the viewport, so we use viewport coordinates (like menu).
   */
  positionLoaderTexts() {
    const loaderTexts = this.textScene.texts.filter(
      (text) => text.name === "loader",
    )
    const { width, height } = this.sizes

    loaderTexts.forEach((text) => {
      text.createBounds()

      const positionX = text.bounds.left - width / 2
      const positionY = height / 2 - text.bounds.top - text.bounds.height

      text.mesh.position.x = positionX
      text.mesh.position.y = positionY

      if (text.proxyMesh) {
        text.proxyMesh.position.x = positionX
        text.proxyMesh.position.y = positionY
      }
    })
  }

  revealTexts() {
    const loaderTexts = this.textScene.texts.filter(
      (text) => text.name === "loader",
    )

    this.positionLoaderTexts()

    const revealables = loaderTexts.map((text) => ({
      reveal: () => text.reveal(),
      top: text.bounds.top,
    }))
    revealables.sort((a, b) => a.top - b.top)
    revealables.forEach((item, index) => {
      gsap.delayedCall(
        index * LoaderSection.REVEAL_STAGGER_DURATION,
        item.reveal,
      )
    })
  }

  leaveTexts() {
    const loaderTexts = this.textScene.texts.filter(
      (text) => text.name === "loader",
    )
    loaderTexts.forEach((text) => text.leave())
  }

  /** Reveal loader texts then about CTA (in sequence). */
  revealTextsAndCTA() {
    this.revealTexts()
    this.aboutCTA.revealAboutCTA()
  }

  /** Leave loader texts and about CTA. */
  leaveTextsAndCTA() {
    this.leaveTexts()
    this.aboutCTA.leaveAboutCTA()
  }

  resetTexts() {
    const loaderTexts = this.textScene.texts.filter(
      (text) => text.name === "loader",
    )
    loaderTexts.forEach((text) => text.resetToRevealStart())
  }

  resize() {
    super.resize()

    this.aboutLeaf.resize()
    // Re-position loader texts from DOM bounds so they stay in sync with the
    // viewport when it changes (e.g. Android URL bar show/hide). Otherwise the
    // About CTA (SmallButton) repositions on resize and can overlap the texts.
    this.positionLoaderTexts()
  }

  update(params: { delta: number; elapsed: number }) {
    super.update(params)

    if (this.interactionManager) {
      this.interactionManager.update()
    }

    if (this.textScene?.interactionManager) {
      this.textScene.interactionManager.update()
    }

    if (this.camera) {
      this.camera.update()
    }

    if (this.highway) {
      this.highway.update()
    }

    if (this.policeCar) {
      this.policeCar.update(params)
    }

    if (this.sky) {
      this.sky.update()
    }

    if (this.man) {
      this.man.update(params)
    }

    if (this.smoke) {
      this.smoke.update()
    }

    if (this.rainWithPuddle) {
      this.rainWithPuddle.update()
    }

    if (this.postProcessing) {
      this.postProcessing.render()
    }

    if (this.textScene) {
      // this.renderer.instance.setRenderTarget(this.textScene.fbo)
      // this.textScene.update(params)
    }

    this.updateCloseTextPosition()
  }

  private updateCloseTextPosition() {
    if (!this.closeText?.textMesh?.visible) {
      return
    }

    const pointer = this.experience.pointer
    if (pointer.primaryInput !== "mouse") {
      this.closeText.leave()
      this.hasSetCloseTextPosition = false
      return
    }
    const { width, height } = this.sizes

    // `TextScene` is set up so that at z=0, world units map to screen pixels.
    // Convert pointer pixels (top-left origin) to centered world coords (y-up).
    const targetX = pointer.x - width / 2 + 18
    const targetY = height / 2 - pointer.y - 18

    if (!this.hasSetCloseTextPosition) {
      this.hasSetCloseTextPosition = true
      this.closeText.textMesh.position.x = targetX
      this.closeText.textMesh.position.y = targetY
      return
    }

    const alpha = 0.12
    this.closeText.textMesh.position.x = lerp(
      this.closeText.textMesh.position.x,
      targetX,
      alpha,
    )
    this.closeText.textMesh.position.y = lerp(
      this.closeText.textMesh.position.y,
      targetY,
      alpha,
    )
  }

  setTextSceneDebug() {
    if (!this.debugFolder) {
      return
    }

    const textSceneFolder = this.debugFolder.addFolder({
      title: "💬 Text Scene",
      expanded: false,
    })

    const emissiveSettings = {
      emissiveIntensity: this.settings.emissiveIntensity.value,
    }
    textSceneFolder
      .addBinding(emissiveSettings, "emissiveIntensity", {
        label: "Emissive intensity",
        min: 0,
        max: 2,
        step: 0.01,
      })
      .on("change", (ev) => {
        const loaderTexts = this.textScene.texts.filter(
          (text) => text.name === "loader",
        )
        loaderTexts.forEach((text) => {
          text.uniforms.emissiveIntensity.value = ev.value
        })
      })
  }

  setDebug() {
    super.setDebug()

    this.debug = this.experience.debug

    if (this.debugFolder) {
      const settings = {
        progress: 0,
      }

      this.debugFolder
        .addBinding(settings, "progress", {
          min: 0,
          max: 1,
          step: 0.01,
        })
        .on("change", (ev) => {
          const loaderTexts = this.textScene.texts.filter(
            (text) => text.name === "loader",
          )
          loaderTexts.forEach((text) => {
            const scrumbledText = text.textScramble(text.text, ev.value)

            text.uniforms.progress.value = ev.value
            text.geometry.update({
              text: scrumbledText,
            })
          })
        })

      this.debugFolder
        .addButton({
          title: "Reveal Texts",
        })
        .on("click", () => {
          this.revealTexts()
        })

      this.debugFolder
        .addButton({
          title: "Reset Texts",
        })
        .on("click", () => {
          this.resetTexts()
        })
    }
  }
}
